import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";
import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";

const EASE = [0.16, 1, 0.3, 1] as const;
const FOREST = "#0A4A3E";
const FOREST_DEEP = "#052E27";
const FOREST_MID = "#6FAE68";
const LEAF = "#8FBE72";
const MINT = "#BFE5D7";
const GOLD = "#E7B84B";
const CORAL = "#E8A89A";
const RUST = "#C77B5E";
const PAPER = "#FCFDF8";
const WHITE = "#FFFFFF";
const INK = "#123D35";
const MUTED = "#60756C";
const LINE = "#D3E0CC";
const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif";

// Chaos particles for the hero
const CHAOS_PARTICLES = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: 4 + Math.random() * 12,
  delay: Math.random() * 3,
  duration: 3 + Math.random() * 4,
  label: ["Fees", "Grades", "Bus", "Meals", "Absent", "Fees", "Stock", "Pay", "Fees", "Grades", "Bus", "Pay"][i % 12],
  angle: Math.random() * 360,
}));

// System nodes that scatter then converge
const SYSTEM_NODES = [
  { label: "Fees", icon: "W", color: GOLD, scatterX: -180, scatterY: -120 },
  { label: "Attendance", icon: "A", color: FOREST, scatterX: 160, scatterY: -140 },
  { label: "Transport", icon: "T", color: MINT, scatterX: -200, scatterY: 80 },
  { label: "Grades", icon: "G", color: LEAF, scatterX: 170, scatterY: 100 },
  { label: "Meals", icon: "M", color: FOREST_MID, scatterX: -120, scatterY: 160 },
  { label: "Procurement", icon: "P", color: GOLD, scatterX: 130, scatterY: 170 },
  { label: "Messages", icon: "C", color: LEAF, scatterX: -50, scatterY: -200 },
  { label: "Payments", icon: "$", color: MINT, scatterX: 60, scatterY: -190 },
];

const PAIN_POINTS = [
  { q: "Where is the fee report?", who: "Headteacher", time: "3 hours" },
  { q: "Did the bus arrive safely?", who: "Parent", time: "Call the driver" },
  { q: "Who still owes fees?", who: "Finance officer", time: "Check 4 spreadsheets" },
  { q: "How much food is left?", who: "Bursar", time: "Walk to the kitchen" },
  { q: "Who was absent today?", who: "Teacher", time: "Paper register" },
  { q: "What did we spend last month?", who: "Headteacher", time: "Ask 3 people" },
];

const IMPACT_STATS = [
  { value: 92, suffix: "%", label: "Fees collected on time", color: GOLD, decimals: 0 },
  { value: 99, suffix: "%", label: "Attendance tracked live", color: FOREST, decimals: 0 },
  { value: 4, suffix: "min", label: "Average report time", color: LEAF, decimals: 0 },
  { value: 0, suffix: "", label: "Spreadsheets needed", color: MINT, decimals: 0 },
];

export function WhyFundredPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: FOREST_DEEP, fontFamily: FS }}>
      <div className="absolute top-0 left-0 right-0 z-50 h-[3px]" style={{ background: `linear-gradient(90deg, ${FOREST}, ${LEAF}, ${GOLD}, ${FOREST})` }} />
      <div className="relative z-10">
        <Navbar />
        <ChaosHero />
        <ScatterConvergeSection />
        <InteractiveToggleSection />
        <PainToAnswerSection />
        <ImpactStatsSection />
        <ConvergenceSection />
        <CTASection />
      </div>
      <Footer />
    </div>
  );
}

// ═══ HERO: Animated chaos particles settling into order ═══
function ChaosHero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 0.6], [0, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSettled(true), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" style={{ background: FOREST_DEEP }}>
      {/* Ambient blobs */}
      <motion.div animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute -left-[10%] top-[15%] h-[55vh] w-[55vh] rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${LEAF}25 0%, transparent 70%)` }} />
      <motion.div animate={{ x: [0, -25, 0], y: [0, 15, 0], opacity: [0.15, 0.4, 0.15] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="pointer-events-none absolute -right-[5%] top-[25%] h-[45vh] w-[45vh] rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(${WHITE}44 1px, transparent 1px), linear-gradient(90deg, ${WHITE}44 1px, transparent 1px)`, backgroundSize: "56px 56px" }} />

      {/* Chaos particles */}
      <div className="pointer-events-none absolute inset-0">
        {CHAOS_PARTICLES.map((p) => (
          <motion.div key={p.id} className="absolute rounded-full" style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size }} initial={{ opacity: 0, scale: 0 }} animate={settled ? { opacity: [0.4, 0, 0.4], scale: [1, 0.5, 1], x: [0, (Math.random() - 0.5) * 60, 0], y: [0, (Math.random() - 0.5) * 60, 0] } : { opacity: [0, 0.5, 0.3], scale: [0, 1.2, 1] }} transition={{ duration: p.duration, repeat: settled ? Infinity : 0, delay: p.delay, ease: "easeInOut" }}>
            <div className="h-full w-full rounded-full" style={{ background: p.id % 3 === 0 ? CORAL : p.id % 3 === 1 ? GOLD : LEAF, opacity: 0.3 }} />
          </motion.div>
        ))}
      </div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center px-4 pt-28 pb-16 text-center sm:px-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-2" style={{ borderColor: `${LEAF}30`, background: `${LEAF}0A` }}>
          <motion.span className="h-2 w-2 rounded-full" style={{ background: LEAF }} animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.28em]" style={{ color: LEAF }}>Why Fundred Exists</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="text-4xl font-bold leading-[0.9] sm:text-6xl lg:text-7xl" style={{ color: WHITE, letterSpacing: "-0.045em" }}>Schools drown in</motion.h1>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7, ease: EASE }} className="text-4xl font-bold leading-[0.9] sm:text-6xl lg:text-7xl" style={{ color: CORAL, letterSpacing: "-0.045em", fontStyle: "italic" }}>fragmented systems.</motion.h1>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.6 }} className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {["Spreadsheets", "WhatsApp groups", "Paper registers", "Notebooks", "Email threads"].map((s, i) => (
            <motion.span key={s} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }} className="rounded-full border px-3 py-1.5 text-[11px] font-medium" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.5)" }}>{s}</motion.span>
          ))}
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 text-xl font-bold sm:text-3xl" style={{ color: WHITE }}>Fundred connects them all.</motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }} className="mt-1 text-base sm:text-xl" style={{ color: LEAF, fontStyle: "italic" }}>One signal. One source of truth.</motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.6 }} className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="h-12 rounded-full px-7 text-[12px] font-bold uppercase tracking-[0.18em]" style={{ background: LEAF, color: FOREST_DEEP, border: "none" }}>
            <Link to="/experience">See the Experience</Link>
          </Button>
          <Button asChild variant="ghost" size="lg" className="h-12 rounded-full px-6 text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: WHITE, border: `1.5px solid ${WHITE}22` }}>
            <Link to="/experience">See the Experience</Link>
          </Button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="flex flex-col items-center gap-1">
          <span className="text-[9px] font-medium uppercase tracking-[0.2em]" style={{ color: `${PAPER}40` }}>Scroll to explore</span>
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={LEAF} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ═══ SCATTER → CONVERGE: Scroll-driven transformation ═══
function ScatterConvergeSection() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const scatterScale = useTransform(scrollYProgress, [0, 0.3, 0.6], [1, 1, 0.8]);
  const convergeScale = useTransform(scrollYProgress, [0.3, 0.6, 1], [0, 0.5, 1]);
  const convergeOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);
  const scatterOpacity = useTransform(scrollYProgress, [0.3, 0.5], [1, 0]);
  const labelOpacity = useTransform(scrollYProgress, [0.15, 0.3, 0.5], [0, 1, 0]);

  return (
    <section ref={ref} className="relative h-[200vh]" style={{ background: FOREST_DEEP }}>
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        {/* Scattered state */}
        <motion.div style={{ scale: scatterScale, opacity: scatterOpacity }} className="absolute inset-0 flex items-center justify-center">
          {SYSTEM_NODES.map((n, i) => (
            <motion.div key={n.label} initial={{ opacity: 0, scale: 0 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }} className="absolute" style={{ transform: `translate(${n.scatterX}px, ${n.scatterY}px)` }}>
              <motion.div animate={{ y: [0, -8, 0], rotate: [0, 3, 0] }} transition={{ duration: 3 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }} className="grid h-14 w-14 place-items-center rounded-2xl border" style={{ borderColor: `${n.color}40`, background: `${n.color}10` }}>
                <span className="text-lg font-bold" style={{ color: n.color }}>{n.icon}</span>
              </motion.div>
              <p className="mt-2 text-center text-[9px] font-medium" style={{ color: "rgba(255,255,255,0.4)" }}>{n.label}</p>
            </motion.div>
          ))}
          {/* Chaotic lines */}
          <svg className="absolute inset-0 h-full w-full" viewBox="-300 -200 600 400">
            {SYSTEM_NODES.map((n, i) => SYSTEM_NODES.slice(i + 1).map((n2, j) => (
              <line key={`${i}-${j}`} x1={n.scatterX} y1={n.scatterY} x2={n2.scatterX} y2={n2.scatterY} stroke="rgba(255,255,255,0.04)" strokeWidth={0.3} strokeDasharray="2 4" />
            )))}
          </svg>
        </motion.div>

        {/* Converged state */}
        <motion.div style={{ scale: convergeScale, opacity: convergeOpacity }} className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Connection lines to center */}
            <svg className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" viewBox="-250 -250 500 500" width="500" height="500">
              {SYSTEM_NODES.map((n, i) => {
                const angle = (i / SYSTEM_NODES.length) * Math.PI * 2 - Math.PI / 2;
                const radius = 180;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;
                return <motion.line key={n.label} x1="0" y1="0" x2={x} y2={y} stroke={n.color} strokeWidth={1} strokeOpacity={0.4} initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.8 }} />;
              })}
            </svg>
            {/* Nodes in circle */}
            {SYSTEM_NODES.map((n, i) => {
              const angle = (i / SYSTEM_NODES.length) * Math.PI * 2 - Math.PI / 2;
              const radius = 180;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              return (
                <div key={n.label} className="absolute" style={{ transform: `translate(${x}px, ${y}px) translate(-50%, -50%)` }}>
                  <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2 + i * 0.2, repeat: Infinity, ease: "easeInOut" }} className="grid h-12 w-12 place-items-center rounded-xl border-2" style={{ borderColor: n.color, background: `${n.color}15`, boxShadow: `0 0 20px ${n.color}30` }}>
                    <span className="text-sm font-bold" style={{ color: n.color }}>{n.icon}</span>
                  </motion.div>
                </div>
              );
            })}
            {/* Center */}
            <motion.div animate={{ boxShadow: [`0 0 30px ${LEAF}20`, `0 0 60px ${LEAF}40`, `0 0 30px ${LEAF}20`] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="relative grid h-24 w-24 place-items-center rounded-3xl" style={{ background: `linear-gradient(135deg, ${FOREST_DEEP}, ${FOREST})`, border: `1.5px solid ${LEAF}45` }}>
              <span className="text-xl font-bold" style={{ color: LEAF }}>F</span>
              <span className="mt-0.5 text-[7px] font-bold uppercase tracking-wider" style={{ color: LEAF }}>Fundred</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Labels */}
        <motion.div style={{ opacity: labelOpacity }} className="absolute bottom-16 left-1/2 -translate-x-1/2 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.4)" }}>8 disconnected systems</p>
        </motion.div>
        <motion.div style={{ opacity: convergeOpacity }} className="absolute bottom-16 left-1/2 -translate-x-1/2 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: LEAF }}>One connected platform</p>
        </motion.div>
      </div>
    </section>
  );
}

// ═══ INTERACTIVE TOGGLE: Without vs With Fundred ═══
function InteractiveToggleSection() {
  const [mode, setMode] = useState<"without" | "with">("without");
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="relative py-20 sm:py-32" style={{ background: FOREST }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${mode === "without" ? CORAL : LEAF}12 0%, transparent 50%)` }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-center">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.5)" }}>The Difference</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: WHITE, letterSpacing: "-0.04em", lineHeight: 0.95 }}>See what changes.</h2>
        </motion.div>

        {/* Toggle */}
        <div className="mt-10 flex justify-center">
          <div className="relative flex rounded-full p-1.5" style={{ border: `1px solid rgba(255,255,255,0.1)`, background: "rgba(255,255,255,0.03)" }}>
            <motion.div className="absolute top-1.5 bottom-1.5 rounded-full" style={{ background: mode === "without" ? CORAL : LEAF }} animate={{ left: mode === "without" ? 6 : "50%", right: mode === "without" ? "50%" : 6 }} transition={{ duration: 0.3, ease: EASE }} />
            <button onClick={() => setMode("without")} className="relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors" style={{ color: mode === "without" ? WHITE : "rgba(255,255,255,0.4)" }}>Without Fundred</button>
            <button onClick={() => setMode("with")} className="relative z-10 px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors" style={{ color: mode === "with" ? FOREST_DEEP : "rgba(255,255,255,0.4)" }}>With Fundred</button>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {mode === "without" ? (
            <motion.div key="without" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3, ease: EASE }} className="mt-12">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {PAIN_POINTS.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.4 }} className="group relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: `${CORAL}25`, background: `${CORAL}08` }}>
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: CORAL }} />
                    <div className="flex items-start gap-3">
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${CORAL}18` }}>
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={CORAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold leading-snug" style={{ color: WHITE }}>{p.q}</p>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: CORAL }}>{p.who}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: `${CORAL}10` }}>
                          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke={CORAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                          <span className="text-[11px] font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{p.time}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
                <p className="text-lg sm:text-2xl" style={{ color: "rgba(255,255,255,0.6)" }}>Every question becomes a <span style={{ color: CORAL, fontStyle: "italic" }}>treasure hunt.</span></p>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div key="with" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3, ease: EASE }} className="mt-12">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {PAIN_POINTS.map((p, i) => {
                  const answers = ["Dashboard shows live collection.", "Bus tracked. Parent notified.", "Outstanding balance: UGX 2.4M.", "Stock supports 4.2 more days.", "87 of 1,200 absent.", "Report ready in 4 minutes."];
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.4 }} className="group relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: `${LEAF}30`, background: `${LEAF}08` }}>
                      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: LEAF }} />
                      <div className="flex items-start gap-3">
                        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${LEAF}18` }}>
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={LEAF} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold leading-snug" style={{ color: WHITE }}>{p.q}</p>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: LEAF }}>{p.who}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: `${LEAF}10` }}>
                            <motion.svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke={LEAF} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}><path d="M13 2L3 14h9l-1 8 10-12h-9z" /></motion.svg>
                            <span className="text-[11px] font-medium" style={{ color: "rgba(255,255,255,0.7)" }}>{answers[i]}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
                <p className="text-lg sm:text-2xl" style={{ color: "rgba(255,255,255,0.6)" }}>Every question already has an <span style={{ color: LEAF, fontStyle: "italic" }}>answer.</span></p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ═══ PAIN → ANSWER: Animated questions getting answered ═══
function PainToAnswerSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveIdx(i => (i + 1) % PAIN_POINTS.length), 2500);
    return () => clearInterval(id);
  }, []);

  const active = PAIN_POINTS[activeIdx];
  const answers = ["Dashboard shows live collection.", "Bus tracked. Parent notified.", "Outstanding balance: UGX 2.4M.", "Stock supports 4.2 more days.", "87 of 1,200 absent.", "Report ready in 4 minutes."];

  return (
    <section ref={ref} className="relative py-20 sm:py-32" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 50%, ${LEAF}08 0%, transparent 60%)` }} />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-center">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.5)" }}>The Transformation</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: WHITE, letterSpacing: "-0.04em", lineHeight: 0.95 }}>From <span style={{ color: CORAL }}>questions</span> to <span style={{ color: LEAF, fontStyle: "italic" }}>answers.</span></h2>
        </motion.div>

        <div className="mt-12 space-y-3">
          {PAIN_POINTS.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <motion.button key={i} onClick={() => setActiveIdx(i)} initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.08, duration: 0.4 }} className="flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all" style={{ borderColor: isActive ? `${LEAF}30` : "rgba(255,255,255,0.06)", background: isActive ? `${LEAF}08` : "rgba(255,255,255,0.02)" }}>
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: isActive ? `${LEAF}18` : "rgba(255,255,255,0.05)" }}>
                  <motion.span className="text-xs font-bold tabular-nums" style={{ color: isActive ? LEAF : "rgba(255,255,255,0.3)" }} animate={{ scale: isActive ? 1.2 : 1 }}>{String(i + 1).padStart(2, "0")}</motion.span>
                </div>
                <div className="flex-1">
                  <AnimatePresence mode="wait">
                    {isActive ? (
                      <motion.div key="answer" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.25 }}>
                        <p className="text-sm font-semibold" style={{ color: WHITE }}>{p.q}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1, repeat: Infinity }} className="h-1.5 w-1.5 rounded-full" style={{ background: LEAF }} />
                          <p className="text-xs font-medium" style={{ color: LEAF }}>{answers[i]}</p>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div key="question" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>{p.q}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <motion.div animate={{ rotate: isActive ? 90 : 0, opacity: isActive ? 1 : 0.3 }}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={isActive ? LEAF : "rgba(255,255,255,0.3)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                </motion.div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ═══ IMPACT STATS: Animated counters ═══
function ImpactStatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="relative py-20 sm:py-32" style={{ background: FOREST }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${GOLD}10 0%, transparent 50%)` }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-center">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.5)" }}>The Impact</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: WHITE, letterSpacing: "-0.04em", lineHeight: 0.95 }}>What changes <span style={{ color: GOLD, fontStyle: "italic" }}>in practice.</span></h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {IMPACT_STATS.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 24, scale: 0.9 }} animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }} whileHover={{ y: -6 }} className="group relative overflow-hidden rounded-2xl border p-6 text-center" style={{ borderColor: `${s.color}25`, background: `${s.color}08` }}>
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `radial-gradient(circle at 50% 50%, ${s.color}12 0%, transparent 70%)` }} />
              <div className="relative">
                <div className="text-3xl font-bold tabular-nums sm:text-5xl" style={{ color: s.color, letterSpacing: "-0.04em" }}>
                  <Counter target={s.value} suffix={s.suffix} decimals={s.decimals} />
                </div>
                <div className="mt-2 text-[10px] font-medium uppercase tracking-wider sm:text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{s.label}</div>
              </div>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full" style={{ background: s.color }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ CONVERGENCE: All signals flowing into one platform ═══
function ConvergenceSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [pulseIdx, setPulseIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setPulseIdx(i => (i + 1) % 5), 2500);
    return () => clearInterval(id);
  }, []);

  const sources = [
    { label: "Attendance", color: FOREST, icon: "A" },
    { label: "Fees", color: GOLD, icon: "$" },
    { label: "Transport", color: MINT, icon: "T" },
    { label: "Procurement", color: LEAF, icon: "P" },
    { label: "Communication", color: FOREST_MID, icon: "C" },
  ];

  return (
    <section ref={ref} className="relative py-20 sm:py-32" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 50%, ${LEAF}10 0%, transparent 60%)` }} />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE }} className="text-center">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.5)" }}>The Architecture</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: WHITE, letterSpacing: "-0.04em", lineHeight: 0.95 }}>Every signal. <span style={{ color: LEAF, fontStyle: "italic" }}>One place.</span></h2>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2, duration: 0.6 }} className="mt-12 overflow-hidden rounded-2xl border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          {/* Source signals */}
          <div className="grid grid-cols-5 gap-px" style={{ background: "rgba(255,255,255,0.06)" }}>
            {sources.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: i * 0.08, duration: 0.3 }} className="relative px-2 py-5 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
                <motion.div animate={{ scale: pulseIdx === i ? 1.2 : 1, boxShadow: pulseIdx === i ? `0 0 20px ${s.color}40` : "none" }} className="mx-auto grid h-10 w-10 place-items-center rounded-xl" style={{ border: `1.5px solid ${s.color}40`, background: `${s.color}10` }}>
                  <span className="text-sm font-bold" style={{ color: s.color }}>{s.icon}</span>
                </motion.div>
                <p className="mt-2 text-[9px] font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>{s.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Flowing pulses */}
          <div className="relative flex justify-center py-4" style={{ background: "rgba(255,255,255,0.01)" }}>
            <svg className="h-16 w-full max-w-md" viewBox="0 0 400 64">
              {sources.map((s, i) => {
                const startX = 40 + i * 80;
                return (
                  <g key={s.label}>
                    <line x1={startX} y1="0" x2="200" y2="32" stroke={`${s.color}30`} strokeWidth="0.5" strokeDasharray="2 3" />
                    {pulseIdx === i && (
                      <motion.circle r="3" fill={s.color} initial={{ cx: startX, cy: 0, opacity: 0 }} animate={{ cx: [startX, 200], cy: [0, 32], opacity: [0, 1, 1, 0] }} transition={{ duration: 1, ease: "easeInOut" }} />
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Center platform */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.5, duration: 0.5 }} className="relative overflow-hidden px-4 py-6 text-center" style={{ background: "rgba(255,255,255,0.04)" }}>
            <motion.div className="absolute inset-0" animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} style={{ background: `radial-gradient(ellipse 60% 100% at 50% 50%, ${LEAF}10 0%, transparent 70%)` }} />
            <div className="relative">
              <motion.div animate={{ boxShadow: [`0 0 20px ${LEAF}20`, `0 0 40px ${LEAF}30`, `0 0 20px ${LEAF}20`] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="mx-auto grid h-16 w-16 place-items-center rounded-2xl" style={{ background: `linear-gradient(135deg, ${FOREST_DEEP}, ${FOREST})`, border: `1.5px solid ${LEAF}45` }}>
                <span className="text-xl font-bold" style={{ color: LEAF }}>F</span>
              </motion.div>
              <p className="mt-3 text-sm font-bold uppercase tracking-[0.2em] sm:text-base" style={{ color: WHITE }}>Fundred</p>
              <p className="mt-1 text-[10px] sm:text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>Every signal converges here.</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Output */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.8, duration: 0.5 }} className="mt-6 flex justify-center gap-3 flex-wrap">
          {["Live dashboards", "AI predictions", "Instant reports", "Smart alerts"].map((o, i) => (
            <motion.span key={o} initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.9 + i * 0.1 }} className="rounded-full border px-4 py-2 text-xs font-medium" style={{ borderColor: `${LEAF}25`, background: `${LEAF}08`, color: LEAF }}>{o}</motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ═══ CTA ═══
function CTASection() {
  return (
    <section className="relative overflow-hidden py-20 sm:py-32" style={{ background: FOREST }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 40% at 50% 50%, ${GOLD}08 0%, transparent 60%)` }} />
      {[...Array(8)].map((_, i) => <motion.div key={i} className="pointer-events-none absolute h-1 w-1 rounded-full" style={{ background: i % 2 === 0 ? GOLD : LEAF, left: `${10 + i * 11}%`, top: `${15 + (i % 4) * 22}%` }} animate={{ y: [0, -25, 0], opacity: [0, 0.5, 0] }} transition={{ duration: 5, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }} />)}
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: WHITE, letterSpacing: "-0.04em", lineHeight: 0.92 }}>Stop chasing.</h2>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: LEAF, letterSpacing: "-0.04em", lineHeight: 0.92, fontStyle: "italic" }}>Start knowing.</h2>
          <p className="mt-6 text-sm sm:text-lg" style={{ color: "rgba(255,255,255,0.5)" }}>One connected platform. Every answer. In real time.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-full px-8 text-[13px] font-bold uppercase tracking-[0.15em]" style={{ background: LEAF, color: FOREST_DEEP, border: "none" }}>
              <Link to="/auth">Get Started</Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-full px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white" style={{ border: "1.5px solid rgba(255,255,255,0.15)" }}>
              <Link to="/experience">See the Experience</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ═══ Counter component ═══
function Counter({ target, suffix = "", decimals = 0 }: { target: number; suffix?: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let frame: number;
    const start = performance.now();
    const duration = 1500;
    const animate = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(target * eased);
      if (t < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [inView, target]);

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toString();
  return <span ref={ref}>{formatted}{suffix}</span>;
}

export default WhyFundredPage;
